import { z } from 'zod';
import { boardParams, endpoint, id, isTimeZone } from './common';

// Calendars connected to a board, from Google (its secret iCal address) or Apple (a calendar shared as a public link). The app reads the address on a
// schedule and makes tasks of its events: a new event becomes a task, an event that changes updates its task, an event that goes takes its task with it.
// The address is never sent back.

export const calendarFeed = z.object({
    id: z.string(),
    name: z.string(),
    // Whose calendar it is.
    provider: z.enum(['google', 'apple']),
    // The section the imported tasks go into.
    sectionId: z.string(),
    // "pending" until it has been read once; "error" when the last reading failed (lastError says why, and it is tried again).
    status: z.enum(['ok', 'error', 'pending']),
    lastSyncAt: z.string().optional(),
    lastSuccessAt: z.string().optional(),
    lastError: z.string().optional(),
    eventCount: z.number().optional(),
    // Something worth knowing about a good reading (the calendar was too big to import in full).
    notice: z.string().optional(),
});
export type CalendarFeed = z.infer<typeof calendarFeed>;

// What one reading did.
export const syncCounts = z.object({ created: z.number(), updated: z.number(), removed: z.number(), events: z.number() });

// `available` is false when this server is not set up for it (then there are no feeds). Only owners and editors may see or change them.
export const getCalendarFeeds = endpoint({
    method: 'GET',
    path: '/boards/:boardId/calendarFeeds',
    params: boardParams,
    response: z.object({ available: z.boolean(), feeds: z.array(calendarFeed), daysAhead: z.number() }),
});

// The address is checked and the calendar read once before anything is saved, so a wrong address is told at once.
export const connectCalendarFeed = endpoint({
    method: 'POST',
    path: '/boards/:boardId/calendarFeeds',
    params: boardParams,
    body: z.object({
        url: z.string().min(1, 'the calendar address is required').max(800, 'that is too long to be a calendar address'),
        sectionId: id,
        timeZone: z.string().max(64).refine(isTimeZone, 'timeZone must be a valid IANA time zone'),
    }),
    response: z.object({ feed: calendarFeed, counts: syncCounts }),
});

export const syncCalendarFeed = endpoint({
    method: 'POST',
    path: '/calendarFeeds/sync',
    body: z.object({ feedId: id }),
    response: z.object({ feed: calendarFeed, counts: syncCounts.optional() }),
});

// Disconnects it. The tasks it made stay as ordinary tasks unless `deleteTasks` is true.
export const disconnectCalendarFeed = endpoint({
    method: 'POST',
    path: '/calendarFeeds/delete',
    body: z.object({ feedId: id, deleteTasks: z.boolean().default(false) }),
    response: z.object({ removedTasks: z.number() }),
});
